import { Box } from '@chakra-ui/react/box';
import { chakra } from '@chakra-ui/react/styled-system';
import { ReactNode, useCallback, useEffect, useRef } from 'react';

interface AuthPageBackgroundProps {
  children?: ReactNode;
  productIntentIllustration?: string;
}

// Parallax multipliers per layer — smaller shapes move less (appear farther away)
const LAYER_SPEEDS = [18, 12, 8, 4];

const TRANSITION_STYLE = { transition: 'transform 0.3s ease-out' };

const AuthPageBackground = ({ children, productIntentIllustration }: AuthPageBackgroundProps) => {
  const layer0 = useRef<SVGGElement>(null);
  const layer1 = useRef<SVGGElement>(null);
  const layer2 = useRef<SVGGElement>(null);
  const layer3 = useRef<SVGGElement>(null);
  const rafId = useRef(0);
  const offset = useRef({ x: 0, y: 0 });

  const applyTransforms = useCallback(() => {
    const { x, y } = offset.current;
    const layers = [layer0, layer1, layer2, layer3];
    layers.forEach((ref, i) => {
      if (ref.current) {
        const speed = LAYER_SPEEDS[i];
        ref.current.style.transform = `translate(${x * speed}px, ${y * speed}px)`;
      }
    });
  }, []);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const handleMouseMove = (e: MouseEvent) => {
      // Normalize to -1..1 from center
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;

      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        offset.current = { x, y };
        applyTransforms();
      });
    };

    const handleOrientation = (e: DeviceOrientationEvent) => {
      const gamma = (e.gamma ?? 0) / 45; // left-right tilt
      const beta = ((e.beta ?? 0) - 45) / 45; // front-back tilt, offset for holding angle

      cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(() => {
        offset.current = {
          x: Math.max(-1, Math.min(1, gamma)),
          y: Math.max(-1, Math.min(1, beta)),
        };
        applyTransforms();
      });
    };

    const supportsOrientation = 'DeviceOrientationEvent' in window;

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    if (supportsOrientation) {
      window.addEventListener('deviceorientation', handleOrientation, { passive: true });
    }

    return () => {
      cancelAnimationFrame(rafId.current);
      window.removeEventListener('mousemove', handleMouseMove);
      if (supportsOrientation) {
        window.removeEventListener('deviceorientation', handleOrientation);
      }
    };
  }, [applyTransforms]);

  return (
    <Box position="relative" width="100vw" height="100vh" overflow="hidden">
      <chakra.svg
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        preserveAspectRatio="xMinYMid slice"
        viewBox="0 0 1512 861"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clipPath="url(#clip0_8570_1197)">
          <rect width="1512" height="861" fill="url(#paint0_linear_8570_1197)" />
          <g ref={layer0} opacity="0.6" filter="url(#filter0_f_8570_1197)" style={TRANSITION_STYLE}>
            <path
              d="M388.784 -262.242L103.536 -524.168C93.1653 -533.53 80.5335 -531.708 71.8586 -523.018C71.2064 -522.374 70.5541 -521.701 69.9454 -520.972L29.6802 -477.72L61.6401 -356.512V-92.9879C61.6401 -58.257 74.772 -25.7405 96.8396 -5.81018L375.478 252.051L341.388 269.15L376.391 299.172L412.221 254.546C417.939 248.435 421.788 239.269 421.788 228.252L423.983 -175.036C423.983 -209.767 410.852 -242.283 388.784 -262.214V-262.242Z"
              fill="#3D125F"
            />
            <path
              d="M343.127 301.554L57.8791 37.7225C35.8115 17.8202 22.6797 -14.7243 22.6797 -49.4553V-454.398C22.6797 -482.177 46.9214 -498.435 64.5755 -482.513L349.824 -218.681C371.891 -198.779 385.023 -166.234 385.023 -131.503V273.467C385.023 301.246 360.781 317.504 343.127 301.582V301.554Z"
              fill="#6A3C98"
            />
          </g>
          <g ref={layer1} opacity="0.6" filter="url(#filter1_f_8570_1197)" style={TRANSITION_STYLE}>
            <path
              d="M1413.21 762.951L1228.06 592.942C1221.33 586.865 1213.13 588.048 1207.5 593.688C1207.08 594.107 1206.65 594.543 1206.26 595.016L1180.12 623.09L1200.87 701.763V872.809C1200.87 895.352 1209.39 916.457 1223.71 929.394L1404.57 1096.76L1382.44 1107.86L1405.16 1127.35L1428.42 1098.38C1432.13 1094.42 1434.63 1088.47 1434.63 1081.32L1436.05 819.554C1436.05 797.011 1427.53 775.905 1413.21 762.969V762.951Z"
              fill="#3D125F"
            />
            <path
              d="M1383.57 1128.9L1198.43 957.649C1184.1 944.731 1175.58 923.608 1175.58 901.065V638.228C1175.58 620.197 1191.31 609.645 1202.77 619.979L1387.92 791.225C1402.24 804.143 1410.77 825.267 1410.77 847.81V1110.66C1410.77 1128.7 1395.03 1139.25 1383.57 1128.91V1128.9Z"
              fill="#6A3C98"
            />
          </g>
          <g ref={layer2} opacity="0.6" filter="url(#filter2_f_8570_1197)" style={TRANSITION_STYLE}>
            <path
              d="M1424.94 -58.2528L1314.1 -160.028C1310.07 -163.666 1305.16 -162.958 1301.79 -159.582C1301.54 -159.331 1301.28 -159.07 1301.05 -158.787L1285.4 -141.98L1297.82 -94.8828V7.51349C1297.82 21.0088 1302.92 33.6436 1311.5 41.3878L1419.77 141.584L1406.52 148.228L1420.12 159.894L1434.04 142.553C1436.26 140.179 1437.76 136.617 1437.76 132.337L1438.61 -24.3676C1438.61 -37.8629 1433.51 -50.4977 1424.94 -58.2419V-58.2528Z"
              fill="#3D125F"
            />
            <path
              d="M1407.19 160.819L1296.36 58.3031C1287.78 50.5698 1282.68 37.9241 1282.68 24.4288V-132.918C1282.68 -143.712 1292.1 -150.029 1298.96 -143.843L1409.8 -41.3266C1418.37 -33.5932 1423.47 -20.9475 1423.47 -7.45227V149.905C1423.47 160.699 1414.05 167.017 1407.19 160.83V160.819Z"
              fill="#6A3C98"
            />
          </g>
          <g ref={layer3} opacity="0.6" filter="url(#filter3_f_8570_1197)" style={TRANSITION_STYLE}>
            <path
              d="M200.948 670.43L154.824 628.077C153.147 626.563 151.105 626.858 149.702 628.263C149.597 628.367 149.491 628.476 149.393 628.594L142.882 635.588L148.05 655.187V697.798C148.05 703.414 150.173 708.672 153.741 711.894L198.797 753.59L193.284 756.355L198.944 761.209L204.738 753.993C205.663 753.005 206.285 751.523 206.285 749.742L206.64 684.531C206.64 678.915 204.517 673.657 200.948 670.434V670.43Z"
              fill="#3D125F"
            />
            <path
              d="M193.566 761.595L147.442 718.934C143.873 715.715 141.75 710.453 141.75 704.837V639.359C141.75 634.867 145.67 632.238 148.524 634.812L194.649 677.474C198.217 680.692 200.34 685.954 200.34 691.57V757.053C200.34 761.545 196.42 764.174 193.566 761.599V761.595Z"
              fill="#6A3C98"
            />
          </g>
        </g>
        <defs>
          <filter
            id="filter0_f_8570_1197"
            x="-117.32"
            y="-670.408"
            width="681.304"
            height="1118.26"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="70" result="effect1_foregroundBlur_8570_1197" />
          </filter>
          <filter
            id="filter1_f_8570_1197"
            x="1105.58"
            y="518.892"
            width="400.475"
            height="684.089"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="35" result="effect1_foregroundBlur_8570_1197" />
          </filter>
          <filter
            id="filter2_f_8570_1197"
            x="1252.68"
            y="-192.453"
            width="215.933"
            height="385.718"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="15" result="effect1_foregroundBlur_8570_1197" />
          </filter>
          <filter
            id="filter3_f_8570_1197"
            x="126.75"
            y="612.068"
            width="94.8901"
            height="165.544"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="7.5" result="effect1_foregroundBlur_8570_1197" />
          </filter>
          <linearGradient
            id="paint0_linear_8570_1197"
            x1="0.460485"
            y1="9.65949e-05"
            x2="1613.66"
            y2="574.018"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#3D125F" />
            <stop offset="1" stopColor="#6A3C98" />
          </linearGradient>
          <clipPath id="clip0_8570_1197">
            <rect width="1512" height="861" fill="white" />
          </clipPath>
        </defs>
      </chakra.svg>
      {productIntentIllustration !== undefined && (
        <Box
          position="absolute"
          top="0"
          left="0"
          width={['100%', '50%']}
          height={['65%', '100%']}
          background="background/primary"
          zIndex="1"
        >
          {productIntentIllustration && (
            <chakra.img
              src={productIntentIllustration}
              alt=""
              position="absolute"
              bottom="0"
              left="0"
              width="100%"
              height="auto"
              transform="translate(-37%, 37%)"
              display={['none', 'block']}
            />
          )}
        </Box>
      )}
      {children && (
        <Box position="relative" zIndex="2" width="100%" height="100%" overflow="auto">
          {children}
        </Box>
      )}
    </Box>
  );
};

export default AuthPageBackground;
